import Image from '@tiptap/extension-image';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { ImageNodeView } from './ImageNodeView';

/**
 * Tiptap's Image extension with a persisted `width` and drag handles.
 *
 * The width is written into the serialized HTML as an inline `style`, because
 * that HTML is what the simulator and shared-flow pages render directly through
 * `dangerouslySetInnerHTML` — they never load Tiptap. An inline style therefore
 * needs no cooperation from those pages beyond a `max-width: 100%` rule so a
 * wide image scales down instead of overflowing a narrow container.
 *
 * Only width is stored; height stays `auto` so the aspect ratio can never be
 * distorted, whatever the render width ends up being.
 */
export const ResizableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,

        parseHTML: (element) => {
          // Accept both forms: our own inline style, and a bare `width`
          // attribute from content pasted in from elsewhere.
          const styleWidth = element.style.width;
          if (styleWidth && styleWidth.endsWith('px')) {
            const parsed = parseInt(styleWidth, 10);
            return Number.isFinite(parsed) ? parsed : null;
          }
          const attrWidth = element.getAttribute('width');
          if (attrWidth) {
            const parsed = parseInt(attrWidth, 10);
            return Number.isFinite(parsed) ? parsed : null;
          }
          return null;
        },

        renderHTML: (attributes) => {
          if (!attributes.width) return {};
          // height:auto travels with the width so downstream pages keep the
          // aspect ratio even if their stylesheet does not say so.
          return { style: `width: ${attributes.width}px; height: auto;` };
        },
      },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageNodeView);
  },
});
